import React, { useState, useRef, useEffect } from "react";
import { useFormikContext } from "formik";
import useFlightBookingStore from "@/lib/useFlightBookingStore";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Locate, MapPin } from "lucide-react";

const defaultSuggestions: Suggestion[] = [
  {
    code: "BLR",
    name: "Bengaluru Intl Arpt",
    city: "Bengaluru",
  },
  {
    code: "DEL",
    name: "Delhi Indira Gandhi Intl",
    city: "Delhi",
  },
  {
    code: "BOM",
    name: "Chhatrapati Shivaji",
    city: "Mumbai",
  },
  {
    code: "MAA",
    name: "Chennai Arpt",
    city: "Chennai",
  },
  {
    code: "GOI",
    name: "Dabolim Arpt",
    city: "Goa In",
  },
  {
    code: "CCU",
    name: "Netaji Subhas Chandra Bose Intl",
    city: "Kolkata",
  },
  {
    code: "PNQ",
    name: "Lohegaon Arpt",
    city: "Pune",
  },
  {
    code: "IXG",
    name: "Sambre Arpt",
    city: "Belgaum",
  },
  {
    code: "CCJ",
    name: "Kozhikode Arpt",
    city: "Kozhikode",
  },
  {
    code: "JAI",
    name: "Sanganeer Arpt",
    city: "Jaipur",
  },
  {
    code: "AMD",
    name: "Sardar Vallabh Bhai Patel Intl Arpt",
    city: "Ahmedabad",
  },
];

interface CityNameProps {
  fromCityOrAirport: string;
  toCityOrAirport: string;
  index?: number;
}

interface Suggestion {
  code: string;
  city: string;
  name: string;
}

interface SuggestionInputProps {
  tripType: "roundtrip" | "oneway" | "multicity";
  isFrom: boolean;
  index: number;
  selectedCityNames: CityNameProps[];
  setSelectedCityNames: React.Dispatch<React.SetStateAction<CityNameProps[]>>;
  onSwap?: () => void;
  onClick?: () => void;
  onSuggestionSelected?: () => void;
  shouldOpen?: boolean;
  onSuggestionClosed?: () => void;
}

const SuggestionInput: React.FC<SuggestionInputProps> = ({
  tripType,
  isFrom,
  index,
  selectedCityNames,
  setSelectedCityNames,
  onSwap,
  onClick,
  onSuggestionSelected,
  shouldOpen,
  onSuggestionClosed,
}) => {
  const [inputValue, setInputValue] = useState<string>("");
  const [suggestions, setSuggestions] =
    useState<Suggestion[]>(defaultSuggestions);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [displayValue, setDisplayValue] = useState<string>("");
  const [subText, setSubText] = useState<string>("");
  const inputRef = useRef<HTMLDivElement>(null);
  const { values, setFieldValue } = useFormikContext<any>();
  const { searchFlightSuggestions }: any = useFlightBookingStore();
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        // Call the callback to reset auto-flow states
        if (onSuggestionClosed) {
          onSuggestionClosed();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Auto-open effect
  useEffect(() => {
    if (shouldOpen) {
      setSuggestions(defaultSuggestions);
      setIsOpen(true);
    }
  }, [shouldOpen]);

  useEffect(() => {
    const currentRoute = values.routeInfos[index];
    const currentCityName = selectedCityNames.find(
      (city) => city.index === index
    );
    console.log(currentRoute, currentCityName);

    if (
      currentRoute?.fromCityOrAirport?.code ||
      currentRoute?.toCityOrAirport?.code
    ) {
      setDisplayValue(
        isFrom
          ? currentCityName?.fromCityOrAirport || ""
          : currentCityName?.toCityOrAirport || ""
      );
      setSubText(
        currentRoute.code
          ? `${currentRoute.code} - ${currentRoute.name || ""}`
          : ""
      );
    } else {
      setDisplayValue("");
      setSubText("");
    }
  }, [values.routeInfos[index], isFrom, index, selectedCityNames]);

  const handleInputChange = async (value: string) => {
    setInputValue(value);
    setIsLoading(true);
    setIsOpen(true);

    try {
      const results = await searchFlightSuggestions(value);
      setSuggestions(results);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = (suggestion: Suggestion) => {
    // console.log(`Selected: ${suggestion.city}` , values);
    setDisplayValue(suggestion.city);
    setSubText(`${suggestion.code} - ${suggestion.name}`);

    setSelectedCityNames((prev) => {
      const newArray = [...prev];
      if (!newArray[index]) {
        newArray[index] = {
          fromCityOrAirport: "",
          toCityOrAirport: "",
          index: index,
        };
      }

      if (isFrom) {
        newArray[index] = {
          ...newArray[index],
          fromCityOrAirport: suggestion.city,
        };
      } else {
        newArray[index] = {
          ...newArray[index],
          toCityOrAirport: suggestion.city,
        };
      }

      return newArray;
    });

    if (isFrom) {
      if (values.routeInfos[index]?.toCityOrAirport?.code === suggestion.code) {
        setLocalError("Source And Destination Cannot Be Same");
        return;
      }

      setFieldValue(`routeInfos[${index}].fromCityOrAirport`, {
        code: suggestion.code,
      });
      if (tripType === "roundtrip") {
        setFieldValue(`routeInfos[1].toCityOrAirport`, {
          code: suggestion.code,
        });
      }
    } else {
      if (
        values.routeInfos[index]?.fromCityOrAirport?.code === suggestion.code
      ) {
        setLocalError("Source And Destination Cannot Be Same");

        return;
      }

      setFieldValue(`routeInfos[${index}].toCityOrAirport`, {
        code: suggestion.code,
      });
      if (tripType === "roundtrip") {
        setFieldValue(`routeInfos[1].fromCityOrAirport`, {
          code: suggestion.code,
        });
      }
    }

    if (
      values.routeInfos[index]?.fromCityOrAirport?.code !== suggestion.code &&
      values.routeInfos[index]?.toCityOrAirport?.code !== suggestion.code
    ) {
      setLocalError(null);
    }

    setIsOpen(false);
    setSuggestions([]);

    // Call the callback to reset auto-flow states
    if (onSuggestionClosed) {
      onSuggestionClosed();
    }

    // Trigger the callback for auto-flow
    if (onSuggestionSelected) {
      onSuggestionSelected();
    }
  };

  return (
    <div
      ref={inputRef}
      className="relative w-[100%] h-[100%]"
      onClick={onClick}
    >
      <TooltipProvider>
        <Tooltip open={!!localError}>
          <TooltipTrigger asChild>
            <div
              onClick={() => {
                setSuggestions(defaultSuggestions);
                setIsOpen(true);
              }}
              className={cn(
                "cursor-pointer rounded-lg p-2 bg-white h-[100%] gap-2 relative",
                "border transition-colors",
                localError ? "border-primary" : "border-gray-200",
                "shadow-sm hover:shadow-md",
                isFrom
                  ? "rounded-tl-[5px] rounded-bl-[5px]"
                  : "rounded-tr-[5px] rounded-br-[5px]"
              )}
            >
              <div className="flex flex-col justify-center pl-2 h-auto">
                <span className="text-primary text-center text-xs font-medium">
                  {isFrom ? "FROM" : "TO"}
                </span>
                {displayValue ? (
                  <div>
                    <div className="text-xl text-gray-900 font-bold h-auto text-center">
                      {displayValue}
                    </div>
                    <div className="text-sm text-gray-500 ">{subText}</div>
                  </div>
                ) : (
                  <div className="text-gray-400 ">
                    {isFrom ? "Enter city or airport" : "Enter city or airport"}
                  </div>
                )}
              </div>
            </div>
          </TooltipTrigger>
          {localError && (
            <TooltipContent className="bg-primary text-white">
              <p>{localError}</p>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>

      {isOpen && (
        <div className="absolute z-50 w-full top-[25px] bg-white rounded-lg shadow-xl border border-gray-200">
          <div className=" border-b">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder={`Enter city or airport`}
              className={cn(
                "w-full p-2 rounded-md",
                "border border-gray-200",
                "text-lg outline-none",
                "placeholder:text-gray-400 text-black text-sm h-auto"
              )}
              autoFocus
            />
          </div>
          <div className="max-h-[49vh] overflow-y-auto scroll-color">
            {isLoading ? (
              <div className="p-2 text-sm text-center text-gray-500">
                {/* <div className="animate-spin w-6 h-4 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div> */}
                Searching...
              </div>
            ) : suggestions?.length === 0 ? (
              <div className="p-2 text-sm text-center text-gray-500">
                No Results Found
              </div>
            ) : (
              suggestions?.map((suggestion, idx) => (
                <div
                  key={idx}
                  onClick={() => handleSelect(suggestion)}
                  className={cn(
                    "p-3 hover:bg-blue-50 cursor-pointer transition-colors",
                    "border-b border-gray-100 last:border-none"
                  )}
                >
                  <div className="flex items-start justify-start gap-3">
                    <MapPin className="text-primary" />

                    <div>
                      <div className="font-bold text-sm text-left text-gray-900">
                        {suggestion.city}
                      </div>
                      <div className="text-xs text-left text-gray-500">
                        {suggestion.code} - {suggestion.name}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SuggestionInput;
